import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-unresolved
import { useAddressLookup } from '@nocn/address-lookup/react';
import InputTitle from './InputTitle';
import { mapLoqateToFields } from '../../helpers/addressMapper';

const LOQATE_CONFIG = {
  apiKey: process.env.REACT_APP_LOQATE_API_KEY,
};

const AddressAutocomplete = ({
  onAddressSelect,
  onNoResults,
  fieldPrefix,
  labelTitle,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [detailsFailed, setDetailsFailed] = useState(false);
  const wrapperRef = useRef(null);

  const { status, suggestions, search, getAddress } = useAddressLookup(LOQATE_CONFIG);

  const noResults = status === 'empty';
  const isLoading = status === 'searching';
  // Provider errors (blocked key, rate limit, outage) must not leave the
  // address fields hidden, or the applicant has no way to continue.
  const lookupFailed = status === 'error' || detailsFailed;

  // Notify parent when no results found or the lookup is unavailable
  useEffect(() => {
    if ((noResults || lookupFailed) && onNoResults) {
      onNoResults();
    }
  }, [noResults, lookupFailed, onNoResults]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { value } = e.target;
    setInputValue(value);
    setHighlightedIndex(-1);
    setDetailsFailed(false);

    if (value.length >= 3) {
      search(value);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = async (suggestion) => {
    // Group suggestions (e.g. a street) expand into individual addresses
    if (suggestion.isGroup) {
      setInputValue(suggestion.label);
      search(suggestion.label, suggestion.id);
      return;
    }

    try {
      const address = await getAddress(suggestion.id);
      if (address) {
        const mappedFields = mapLoqateToFields(address, fieldPrefix);
        onAddressSelect(mappedFields);
      }
      setInputValue('');
      setShowSuggestions(false);
    } catch (err) {
      console.error('Failed to get address details:', err);
      setDetailsFailed(true);
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (prev < suggestions.length - 1) return prev + 1;
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const handleSuggestionKeyDown = (e, suggestion) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelectSuggestion(suggestion);
    }
  };

  return (
    <div className="address-autocomplete" ref={wrapperRef}>
      <label>
        <InputTitle>{labelTitle}</InputTitle>
        <input
          type="text"
          className="textInput"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder="Start typing an address..."
          autoComplete="off"
        />
      </label>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="autocomplete-suggestions" role="listbox">
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              role="option"
              aria-selected={index === highlightedIndex}
              tabIndex={0}
              className={`autocomplete-suggestion ${
                index === highlightedIndex ? 'highlighted' : ''
              }`}
              onClick={() => handleSelectSuggestion(suggestion)}
              onKeyDown={(e) => handleSuggestionKeyDown(e, suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion.label}
              {/* Town and postcode: without it same-street results look identical */}
              {suggestion.detail && (
                <span className="autocomplete-suggestion__detail">
                  {`, ${suggestion.detail}`}
                </span>
              )}
              {suggestion.isGroup && ' \u203a'}
            </li>
          ))}
        </ul>
      )}

      {isLoading && (
        <div className="autocomplete-loading">Searching...</div>
      )}

      {noResults && !isLoading && showSuggestions && (
        <div className="autocomplete-no-results">
          No addresses found. Please enter your address manually below.
        </div>
      )}

      {lookupFailed && !isLoading && (
        <div className="autocomplete-unavailable" role="alert">
          Address search is not available right now. Please enter your
          address manually below.
        </div>
      )}
    </div>
  );
};

AddressAutocomplete.propTypes = {
  onAddressSelect: PropTypes.func.isRequired,
  onNoResults: PropTypes.func,
  fieldPrefix: PropTypes.string,
  labelTitle: PropTypes.string,
};

AddressAutocomplete.defaultProps = {
  onNoResults: null,
  fieldPrefix: '',
  labelTitle: 'Search Address',
};

export default AddressAutocomplete;
